import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, Package, Truck, CheckCircle, Clock, MapPin,
  Loader2, ExternalLink, AlertCircle, ShoppingBag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { StatusBadge } from '../components/ui/status-badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

const STATUS_CONFIG = {
  pending: { label: 'Order Received', icon: Clock, color: 'bg-yellow-100 text-yellow-800', semantic: 'warning', step: 1 },
  processing: { label: 'Processing', icon: Package, color: 'bg-blue-100 text-blue-800', semantic: 'info', step: 2 },
  shipped: { label: 'Shipped', icon: Truck, color: 'bg-purple-100 text-purple-800', semantic: 'info', step: 3 },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'bg-green-100 text-green-800', semantic: 'success', step: 4 },
  ready_for_collection: { label: 'Ready for Collection', icon: MapPin, color: 'bg-emerald-100 text-emerald-800', semantic: 'success', step: 3 },
  collected: { label: 'Collected', icon: CheckCircle, color: 'bg-green-100 text-green-800', semantic: 'success', step: 4 },
  cancelled: { label: 'Cancelled', icon: AlertCircle, color: 'bg-red-100 text-red-800', semantic: 'error', step: 0 }
};

export default function OrderTrackingPage() {
  const [email, setEmail] = useState('');
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !orderId.trim()) {
      toast.error('Please enter both email and order ID');
      return;
    }
    
    setLoading(true);
    setError('');
    setOrder(null);
    
    try {
      const res = await fetch(`${API_URL}/api/shop/orders/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          order_id: orderId.trim()
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setOrder(data);
      } else {
        const err = await res.json();
        setError(err.detail || 'Order not found. Please check your details.');
      }
    } catch (err) {
      console.error('Track order error:', err);
      setError('Failed to look up order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  const isCollectionOrder = order?.shipping_method === 'collection';

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-violet-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Track Your Order</h1>
            <p className="text-slate-600">Enter your details to check your order status</p>
          </motion.div>
        </div>

        {/* Search Form */}
        {!order && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Find Your Order</CardTitle>
                <CardDescription>
                  Enter the email address you used during checkout and your order ID
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleTrackOrder} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="orderId">Order ID</Label>
                    <Input
                      id="orderId"
                      value={orderId}
                      onChange={(e) => setOrderId(e.target.value)}
                      placeholder="e.g., ord_abc123..."
                      className="font-mono"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      You can find this in your order confirmation email
                    </p>
                  </div>
                  
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}
                  
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Looking up order...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Track Order
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Order Details */}
        {order && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Status Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center mb-6">
                  <StatusBadge status={getStatusConfig(order.fulfillment_status).semantic} className="text-sm px-4 py-1">
                    {getStatusConfig(order.fulfillment_status).label}
                  </StatusBadge>
                  <p className="text-sm text-slate-500 mt-2">
                    Order #{order.order_id.slice(0, 12)}
                  </p>
                </div>

                {/* Progress Steps */}
                {order.fulfillment_status !== 'cancelled' && (
                  <div className="relative">
                    <div className="flex justify-between items-center mb-2">
                      {(isCollectionOrder ? 
                        [
                          { step: 1, label: 'Received', icon: Clock },
                          { step: 2, label: 'Processing', icon: Package },
                          { step: 3, label: 'Ready', icon: MapPin },
                          { step: 4, label: 'Collected', icon: CheckCircle }
                        ] : 
                        [
                          { step: 1, label: 'Received', icon: Clock },
                          { step: 2, label: 'Processing', icon: Package },
                          { step: 3, label: 'Shipped', icon: Truck },
                          { step: 4, label: 'Delivered', icon: CheckCircle }
                        ]
                      ).map((item, idx) => {
                        const currentStep = getStatusConfig(order.fulfillment_status).step;
                        const isActive = item.step <= currentStep;
                        const Icon = item.icon;
                        
                        return (
                          <div key={idx} className="flex flex-col items-center relative z-10">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              isActive ? 'bg-violet-600 text-white' : 'bg-slate-200 text-slate-400'
                            }`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <span className={`text-xs mt-2 ${isActive ? 'text-violet-600 font-medium' : 'text-slate-400'}`}>
                              {item.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Progress Line */}
                    <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 -z-0" style={{ marginLeft: '40px', marginRight: '40px' }}>
                      <div 
                        className="h-full bg-violet-600 transition-all duration-500"
                        style={{ width: `${((getStatusConfig(order.fulfillment_status).step - 1) / 3) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Tracking Info */}
                {order.tracking_number && (
                  <div className="mt-6 p-4 bg-violet-50 rounded-lg">
                    <p className="text-sm font-medium text-violet-800 mb-1">Tracking Number</p>
                    <p className="font-mono text-violet-900">{order.tracking_number}</p>
                    {order.tracking_url && (
                      <a 
                        href={order.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-violet-600 hover:underline mt-2"
                      >
                        Track Package <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                )}

                {/* Collection Info */}
                {isCollectionOrder && order.fulfillment_status === 'ready_for_collection' && (
                  <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm font-medium text-emerald-800 mb-2 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Ready for Collection
                    </p>
                    <p className="text-sm text-emerald-700">
                      Your order is ready! Please collect from our location. Bring your order confirmation email or ID.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items */}
                <div className="space-y-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-slate-900">{item.name}</p>
                        <p className="text-sm text-slate-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-medium">{formatCurrency(item.price * item.quantity)}</p>
                    </div>
                  ))}
                </div>

                <Separator />

                {/* Totals */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  {order.shipping_cost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Shipping</span>
                      <span>{formatCurrency(order.shipping_cost)}</span>
                    </div>
                  )}
                  {order.shipping_method === 'collection' && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Shipping</span>
                      <span className="text-green-600">Collection (Free)</span>
                    </div>
                  )}
                  {order.discount_amount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-{formatCurrency(order.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base pt-2 border-t">
                    <span>Total</span>
                    <span>{formatCurrency(order.total_amount)}</span>
                  </div>
                </div>

                {/* Shipping/Collection Address */}
                {order.shipping_address && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      {isCollectionOrder ? 'Contact Details' : 'Delivery Address'}
                    </p>
                    <p className="text-sm text-slate-600">
                      {order.shipping_address.name}<br />
                      {!isCollectionOrder && (
                        <>
                          {order.shipping_address.line1}<br />
                          {order.shipping_address.line2 && <>{order.shipping_address.line2}<br /></>}
                          {order.shipping_address.city}, {order.shipping_address.postcode}<br />
                          {order.shipping_address.country}
                        </>
                      )}
                    </p>
                  </div>
                )}

                {/* Order Date */}
                <div className="pt-4 border-t text-sm text-slate-500">
                  <p>Order placed: {formatDate(order.created_at)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Track Another Order */}
            <div className="text-center">
              <Button 
                variant="outline" 
                onClick={() => {
                  setOrder(null);
                  setEmail('');
                  setOrderId('');
                }}
              >
                Track Another Order
              </Button>
            </div>
          </motion.div>
        )}

        {/* Help Text */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Need help? Contact us at{' '}
            <a href="mailto:shop@linedancecentral.uk" className="text-violet-600 hover:underline">
              shop@linedancecentral.uk
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
